import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion/dist/framer-motion";
import { useGame } from "../GameState";
import { SFX, resumeAudio } from "../sfx";
import HUD, { AchievementPopup } from "../components/HUD";
import GameControls from "../components/GameControls";

const AVEN_CALM = process.env.PUBLIC_URL + "/mothersday/aven-cutout.png";
const AVEN_LAUGH = process.env.PUBLIC_URL + "/mothersday/aven-laughing-cutout.png";

/* tunables */
const TARGET_SCORE = 200;
const DURATION_S = 60;
const SPAWN_BASE_MS = 750;
const SPAWN_MIN_MS = 280;
const FALL_BASE_S = 4.4;
const FALL_MIN_S = 2.0;
const AVEN_W = 110;
const AVEN_HALF = AVEN_W / 2;
const CATCH_HEIGHT = 90;
const POSE_LAUGH_MS = 600;

const KINDS = [
  { id: "heart",  emoji: "❤",  weight: 6, points: 10, sfx: "catch",  good: true },
  { id: "flower", emoji: "❀",  weight: 4, points: 15, sfx: "flower", good: true },
  { id: "kiss",   emoji: "💋", weight: 2, points: 25, sfx: "photo",  good: true },
  { id: "gold",   emoji: "🌟", weight: 1, points: 50, sfx: "photo",  good: true },
  { id: "bomb",   emoji: "☁",  weight: 3, points: 0,  sfx: "bomb",   good: false },
];

const pickKind = () => {
  const total = KINDS.reduce((s, k) => s + k.weight, 0);
  let r = Math.random() * total;
  for (const k of KINDS) {
    if ((r -= k.weight) <= 0) return k;
  }
  return KINDS[0];
};

let nextId = 1;
const newGame = () => ({
  items: [],         // {id,kind,xFrac,startedAt,duration}
  floats: [],        // {id,x,y,text,color,ts}
  avenX: 0.5,        // 0..1 across stage
  score: 0,
  combo: 0,
  bestCombo: 0,
  lives: 3,
  startedAt: 0,
  timer: DURATION_S,
  shake: 0,
  pose: "calm",
  poseUntil: 0,
  lastSpawnAt: 0,
});

const CatchAct = ({ onNext }) => {
  const { addScore, clearLevel, addAchievement } = useGame();
  const [phase, setPhase] = useState("ready"); // ready | playing | won | lost
  const [, forceRender] = useState(0);
  const stageRef = useRef(null);
  const stageW = useRef(800);
  const stageH = useRef(560);
  const gRef = useRef(newGame());
  const keysRef = useRef({ left: false, right: false });
  const phaseRef = useRef(phase);

  useEffect(() => { phaseRef.current = phase; }, [phase]);

  /* measure stage */
  useEffect(() => {
    const measure = () => {
      if (!stageRef.current) return;
      const r = stageRef.current.getBoundingClientRect();
      stageW.current = r.width;
      stageH.current = r.height;
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [phase]);

  /* keyboard + pointer input — always on while mounted */
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === "ArrowLeft" || e.key === "a" || e.key === "A") keysRef.current.left = true;
      if (e.key === "ArrowRight" || e.key === "d" || e.key === "D") keysRef.current.right = true;
    };
    const onKeyUp = (e) => {
      if (e.key === "ArrowLeft" || e.key === "a" || e.key === "A") keysRef.current.left = false;
      if (e.key === "ArrowRight" || e.key === "d" || e.key === "D") keysRef.current.right = false;
    };
    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("keyup", onKeyUp);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keyup", onKeyUp);
    };
  }, []);

  const onPointerMove = (e) => {
    if (phaseRef.current !== "playing") return;
    if (!stageRef.current) return;
    const r = stageRef.current.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const x = clientX - r.left;
    gRef.current.avenX = Math.max(0, Math.min(1, x / r.width));
  };

  /* main game loop — single rAF for entire game */
  useEffect(() => {
    if (phase !== "playing") return;
    let raf;
    let last = performance.now();
    gRef.current.startedAt = last;
    gRef.current.lastSpawnAt = last;

    const loop = (now) => {
      const dt = Math.min(0.04, (now - last) / 1000);
      last = now;
      const g = gRef.current;
      const SW = stageW.current;
      const SH = stageH.current;

      /* horizontal control via keys (pointer is direct-set in handler) */
      const keys = keysRef.current;
      const v = 1.05; // fraction of stage per second
      if (keys.left) g.avenX = Math.max(0, g.avenX - v * dt);
      if (keys.right) g.avenX = Math.min(1, g.avenX + v * dt);

      /* timer */
      const elapsed = (now - g.startedAt) / 1000;
      g.timer = Math.max(0, Math.ceil(DURATION_S - elapsed));

      /* difficulty ramp */
      const diff = Math.min(1, elapsed / 45);
      const spawnInterval = SPAWN_BASE_MS - (SPAWN_BASE_MS - SPAWN_MIN_MS) * diff;
      const fallDur = FALL_BASE_S - (FALL_BASE_S - FALL_MIN_S) * diff;

      /* spawn */
      if (now - g.lastSpawnAt >= spawnInterval) {
        g.lastSpawnAt = now;
        const kind = pickKind();
        g.items.push({
          id: nextId++,
          kind,
          x: 30 + Math.random() * (SW - 60),
          startedAt: now,
          duration: fallDur * 1000,
        });
      }

      /* update items, detect collisions */
      const survivors = [];
      const avenPxX = g.avenX * SW;
      const catchYTop = SH - CATCH_HEIGHT - 40;
      const catchYBot = SH - 40;
      let scoreDelta = 0;
      let goodCaught = 0;
      let bombHit = false;
      let goodMissed = 0;

      for (const it of g.items) {
        const t = (now - it.startedAt) / it.duration;
        if (t >= 1.05) {
          if (it.kind.good) goodMissed += 1;
          continue;
        }
        const y = -40 + (SH + 60) * t;
        it.y = y; // for render
        const inBand = y >= catchYTop && y <= catchYBot;
        const inX = Math.abs(it.x - avenPxX) < AVEN_HALF - 6;
        if (inBand && inX) {
          if (it.kind.good) {
            goodCaught += 1;
            scoreDelta += it.kind.points;
            SFX[it.kind.sfx] && SFX[it.kind.sfx]();
            g.floats.push({
              id: nextId++,
              x: it.x,
              y: SH - CATCH_HEIGHT - 50,
              text: `+${it.kind.points}`,
              color: it.kind.id === "gold" ? "#ffd93d" : "#ff5a8a",
              ts: now,
            });
          } else {
            bombHit = true;
            SFX.bomb();
            SFX.loseLife();
            g.shake = now;
          }
          continue; // consumed
        }
        survivors.push(it);
      }
      g.items = survivors;

      /* score / combo / lives */
      if (goodCaught) {
        g.combo += goodCaught;
        if (g.combo > g.bestCombo) g.bestCombo = g.combo;
        const mult = 1 + Math.min(2, Math.floor(g.combo / 5) * 0.5);
        g.score += Math.round(scoreDelta * mult);
        g.pose = "laugh";
        g.poseUntil = now + POSE_LAUGH_MS;
      }
      if (bombHit) {
        g.lives = Math.max(0, g.lives - 1);
        g.combo = 0;
      }
      if (goodMissed) g.combo = 0;

      /* pose timeout */
      if (g.pose === "laugh" && now > g.poseUntil) g.pose = "calm";

      /* prune floats older than 900ms */
      g.floats = g.floats.filter((f) => now - f.ts < 900);

      /* end conditions */
      if (g.lives <= 0) { setPhase("lost"); return; }
      if (g.score >= TARGET_SCORE) { setPhase("won"); return; }
      if (g.timer <= 0) { setPhase(g.score >= TARGET_SCORE ? "won" : "lost"); return; }

      /* render tick */
      forceRender((t) => (t + 1) | 0);
      raf = requestAnimationFrame(loop);
    };

    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [phase]);

  /* commit results on win */
  useEffect(() => {
    if (phase !== "won") return;
    const g = gRef.current;
    addScore(g.score);
    clearLevel("catch", 1);
    addAchievement("catcher", "Heart Catcher", `Cleared Level 1 with ${g.score} points`);
    if (g.bestCombo >= 10) addAchievement("combo10", "Combo King", `${g.bestCombo}× combo streak`);
    if (g.lives === 3) addAchievement("noHit", "Untouchable", "No life lost in Level 1");
    SFX.win();
  }, [phase, addScore, clearLevel, addAchievement]);

  useEffect(() => {
    if (phase === "lost") SFX.lose();
  }, [phase]);

  /* start */
  const start = () => {
    resumeAudio();
    SFX.click();
    gRef.current = newGame();
    setPhase("playing");
  };

  /* render — read directly from gRef */
  const g = gRef.current;
  const SW = stageW.current;
  const SH = stageH.current;
  const showShake = g.shake && performance.now() - g.shake < 320;

  return (
    <motion.div
      className="md-act md-act-game md-act-catch"
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -16 }}
    >
      <HUD level={1} levelLabel="Catch the Hugs" lives={g.lives} combo={g.combo} timer={phase === "playing" ? g.timer : DURATION_S} />
      <AchievementPopup />

      <motion.div
        ref={stageRef}
        className="md-game-stage md-catch-stage"
        animate={showShake ? { x: [0, -8, 7, -5, 0] } : { x: 0 }}
        transition={{ duration: 0.32 }}
        onMouseMove={onPointerMove}
        onTouchMove={onPointerMove}
        onTouchStart={onPointerMove}
      >
        <div className="md-catch-clouds">
          <span className="md-cloud md-cloud-1">☁</span>
          <span className="md-cloud md-cloud-2">☁</span>
          <span className="md-cloud md-cloud-3">☁</span>
        </div>

        {phase === "playing" && (
          <GameControls
            onSkip={onNext}
            controls={[
              { keys: ["←", "→"], label: "Move Aven left / right" },
              { keys: ["A", "D"], label: "Same as arrows" },
              { keys: ["mouse", "drag"], label: "Or just move your mouse / finger" },
              { label: "❤ ❀ 💋 = catch them all · 🌟 = bonus · ☁ = avoid!" },
              { label: "Build a combo of 5+ for a score multiplier" },
            ]}
          />
        )}

        {phase === "playing" && g.items.map((it) => (
          <div
            key={it.id}
            className={`md-fall md-fall-${it.kind.id}`}
            style={{ left: it.x, top: it.y || 0 }}
          >
            {it.kind.emoji}
          </div>
        ))}

        {phase === "playing" && g.floats.map((f) => {
          const age = Math.min(1, (performance.now() - f.ts) / 850);
          return (
            <div
              key={f.id}
              className="md-float-text"
              style={{
                left: f.x,
                top: f.y - age * 60,
                color: f.color,
                opacity: 1 - age,
                transform: `scale(${0.9 + age * 0.4})`,
              }}
            >
              {f.text}
            </div>
          );
        })}

        <div
          className="md-catcher"
          style={{
            left: `${g.avenX * 100}%`,
            transform: `translateX(-${AVEN_HALF}px)`,
            width: AVEN_W,
            visibility: phase === "playing" ? "visible" : "hidden",
          }}
        >
          <img
            src={g.pose === "laugh" ? AVEN_LAUGH : AVEN_CALM}
            alt="Aven"
            className="md-catcher-img"
          />
          <div className="md-catcher-basket" />
        </div>

        <AnimatePresence>
          {phase === "ready" && (
            <motion.div className="md-game-overlay" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <div className="md-game-card">
                <div className="md-game-badge">Level 1 of 4</div>
                <h2>Catch the Hugs</h2>
                <p className="md-game-tagline">
                  Mama's hugs are falling from the sky.<br />
                  Catch <strong>{TARGET_SCORE}</strong> points before time runs out.
                </p>
                <ul className="md-game-rules">
                  <li><span>❤</span> heart <em>+10</em></li>
                  <li><span>❀</span> flower <em>+15</em></li>
                  <li><span>💋</span> kiss <em>+25</em></li>
                  <li><span style={{ color: '#ffd93d' }}>🌟</span> golden <em>+50</em></li>
                  <li><span>☁</span> storm cloud <em>−1 ♥</em></li>
                </ul>
                <p className="md-game-hint">
                  Move with <kbd>←</kbd> <kbd>→</kbd> · or your mouse · or your finger.<br />
                  <strong>5+ combo</strong> = score multiplier.
                </p>
                <div className="md-game-actions">
                  <button className="md-btn-soft md-cta" onClick={start}>Start Level 1 →</button>
                  <button className="md-btn-soft md-btn-ghost" onClick={onNext}>Skip</button>
                </div>
              </div>
            </motion.div>
          )}

          {phase === "won" && (
            <motion.div className="md-game-overlay" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              <div className="md-game-card md-game-win">
                <div className="md-game-badge md-badge-gold">Level cleared</div>
                <h2>Mama's hugs received ❤</h2>
                <div className="md-game-stats">
                  <div><span>Score</span><strong>{g.score}</strong></div>
                  <div><span>Best combo</span><strong>×{g.bestCombo}</strong></div>
                  <div><span>Lives left</span><strong>{g.lives} / 3</strong></div>
                </div>
                <p className="md-game-aven-says">
                  ‘Mama, I caught {g.score} points worth of hugs for you.’
                </p>
                <div className="md-game-actions">
                  <button className="md-btn-soft md-btn-ghost" onClick={start}>↻ Replay</button>
                  <button className="md-btn-soft md-cta" onClick={onNext}>Next: Level 2 →</button>
                </div>
              </div>
            </motion.div>
          )}

          {phase === "lost" && (
            <motion.div className="md-game-overlay" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              <div className="md-game-card md-game-lose">
                <div className="md-game-badge md-badge-grey">Time / lives out</div>
                <h2>Almost!</h2>
                <p>You caught <strong>{g.score}</strong> of <strong>{TARGET_SCORE}</strong> needed.</p>
                <div className="md-game-actions">
                  <button className="md-btn-soft md-cta" onClick={start}>↻ Try again</button>
                  <button className="md-btn-soft md-btn-ghost" onClick={onNext}>Skip this level</button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </motion.div>
  );
};

export default CatchAct;
